import React, { useState, useEffect } from 'react';
import { User, Briefcase, Truck, Settings, ChevronDown, Check, Info } from 'lucide-react';

const ROLE_TEMPLATE_META = {
  sales_rep: {
    icon: Briefcase,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    selectedBg: 'bg-blue-50 border-blue-500',
    description: 'Sales representative with access to quotations, orders, invoices, returns and payments'
  },
  cashier: {
    icon: User,
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    selectedBg: 'bg-purple-50 border-purple-500',
    description: 'Cashier with POS access, payments and basic invoice viewing'
  },
  delivery: {
    icon: Truck,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    selectedBg: 'bg-amber-50 border-amber-500',
    description: 'Delivery staff with access to delivery challans and order viewing'
  },
  custom: {
    icon: Settings,
    color: 'bg-slate-100 text-slate-700 border-slate-200',
    selectedBg: 'bg-slate-50 border-slate-500',
    description: 'Custom permissions - configure manually in Access Controls tab'
  }
};

const ProfileTab = ({ formData, setFormData, isEditMode, roleTemplates = [] }) => {
  const [showTemplateInfo, setShowTemplateInfo] = useState(false);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRoleTemplateSelect = (templateId) => {
    const template = roleTemplates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        role_template: templateId,
        permissions: template.permissions
      }));
    }
  };

  const currentTemplate = formData.role_template || 'custom';
  const templateMeta = ROLE_TEMPLATE_META[currentTemplate] || ROLE_TEMPLATE_META.custom;

  return (
    <div className="space-y-6">
      {/* Role Template Selection - Only for new staff */}
      {!isEditMode && (
        <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-teal-600" />
              Role Template <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setShowTemplateInfo(!showTemplateInfo)}
              className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1"
            >
              <Info className="w-3.5 h-3.5" />
              {showTemplateInfo ? 'Hide Info' : 'Learn More'}
            </button>
          </div>
          
          {showTemplateInfo && (
            <div className="mb-4 p-3 bg-white/80 rounded-lg border border-teal-100 text-xs text-slate-600">
              <p className="mb-2"><strong>Role templates</strong> pre-configure permissions based on common job functions:</p>
              <ul className="space-y-1 ml-4 list-disc">
                <li><strong>Sales Rep:</strong> Full access to sales workflow (quotes, orders, invoices, payments)</li>
                <li><strong>Cashier/POS:</strong> POS terminal access, invoice creation, payment collection</li>
                <li><strong>Delivery:</strong> Delivery challans, order viewing, payment collection</li>
                <li><strong>Custom:</strong> Configure individual permissions manually</li>
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {roleTemplates.map((template) => {
              const meta = ROLE_TEMPLATE_META[template.id] || ROLE_TEMPLATE_META.custom;
              const Icon = meta.icon;
              const isSelected = currentTemplate === template.id;
              
              return (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => handleRoleTemplateSelect(template.id)}
                  data-testid={`role-template-${template.id}`}
                  className={`relative flex flex-col items-center p-4 rounded-lg border-2 transition-all duration-200 ${
                    isSelected 
                      ? `${meta.selectedBg} shadow-md scale-[1.02]` 
                      : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <Check className="w-4 h-4 text-teal-600" />
                    </div>
                  )}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${meta.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={`text-sm font-medium ${isSelected ? 'text-slate-800' : 'text-slate-600'}`}>
                    {template.name}
                  </span>
                </button>
              );
            })}
          </div>
          
          {currentTemplate && (
            <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5" />
              {templateMeta.description}
            </p>
          )}
        </div>
      )}

      {/* Show current role for edit mode */}
      {isEditMode && formData.role_template && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${templateMeta.color}`}>
            {React.createElement(templateMeta.icon, { className: 'w-4 h-4' })}
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700">
              Role: {ROLE_TEMPLATE_META[formData.role_template]?.icon ? 
                roleTemplates.find(t => t.id === formData.role_template)?.name || 'Custom' : 'Custom'}
            </p>
            <p className="text-xs text-slate-500">
              {templateMeta.description}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Full Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.full_name}
            onChange={(e) => handleChange('full_name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="Enter full name"
            data-testid="staff-full-name"
          />
        </div>

        {/* Mobile */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mobile <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formData.mobile}
            onChange={(e) => handleChange('mobile', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="Enter mobile number"
            data-testid="staff-mobile"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="Enter email address (used for login)"
            data-testid="staff-email"
          />
          <p className="text-xs text-gray-500 mt-1">Staff can login with email or mobile</p>
        </div>

        {/* Employee Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Employee Code
          </label>
          <input
            type="text"
            value={formData.employee_code}
            onChange={(e) => handleChange('employee_code', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="e.g., EMP001"
            data-testid="staff-employee-code"
          />
        </div>

        {/* Date of Joining */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date of Joining
          </label>
          <input
            type="date"
            value={formData.date_of_joining}
            onChange={(e) => handleChange('date_of_joining', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            data-testid="staff-doj"
          />
        </div>

        {/* Password (only for new staff) */}
        {!isEditMode && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Enter password"
              data-testid="staff-password"
            />
            <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          placeholder="Add any notes about this staff member..."
          data-testid="staff-notes"
        />
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Status
        </label>
        <div className="flex items-center space-x-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              value="active"
              checked={formData.status === 'active'}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
              data-testid="staff-status-active"
            />
            <span className="ml-2 text-sm text-gray-700">Active</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              value="inactive"
              checked={formData.status === 'inactive'}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
              data-testid="staff-status-inactive"
            />
            <span className="ml-2 text-sm text-gray-700">Inactive</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;
